import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, setActiveBusiness, setToken } from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const MODULES = ["dashboard", "properties", "tenants", "owners", "agreements", "invoices", "payments", "approvals", "outstanding", "expenses", "deposits", "banking", "reports", "tenant_sheet", "calendar", "notifications", "documents", "backup", "settings", "users"];
const ALL = MODULES.flatMap((m) => [`${m}.view`, `${m}.create`, `${m}.edit`, `${m}.delete`, `${m}.cancel`]);
const FIN_CRUD = ["invoices", "payments", "approvals", "expenses", "deposits", "agreements", "documents", "banking"].flatMap((m) => [`${m}.create`, `${m}.edit`]);
const ACCT = [...MODULES.filter((m) => m !== "users" && m !== "settings" && m !== "backup").map((m) => `${m}.view`), ...FIN_CRUD, "invoices.cancel"];
const STAFF = ["dashboard.view", "properties.view", "tenants.view", "agreements.view", "invoices.view", "payments.view", "payments.create", "calendar.view", "notifications.view", "documents.view"];
const rolePerms = (r) => (r === "owner" || r === "admin") ? ALL : r === "accountant" ? ACCT : r === "staff" ? STAFF : [];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [activeId, setActiveId] = useState(localStorage.getItem("business_id") || "");
  const [idleMinutes, setIdleMinutesState] = useState(Number(localStorage.getItem("idle_minutes") || 5));
  const lastActivity = useRef(Date.now());

  const setIdleMinutes = (m) => { localStorage.setItem("idle_minutes", String(m)); setIdleMinutesState(Number(m)); toast.success(`Auto-logout set to ${m} min of inactivity`); };

  const pickActive = (list) => {
    if (sessionStorage.getItem("force_onboarding") === "1") { setActiveBusiness(""); setActiveId(""); return ""; }
    const stored = localStorage.getItem("business_id");
    const valid = list.find((b) => b.id === stored);
    const chosen = valid ? stored : (list[0]?.id || "");
    localStorage.setItem("business_id", chosen || "");
    setActiveBusiness(chosen); setActiveId(chosen);
    return chosen;
  };

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — skip /auth/me so the callback exchanges session_id first.
    if (window.location.hash?.includes("session_id=")) return;
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setUser({ id: me.id, email: me.email, name: me.name, is_super_admin: me.is_super_admin, subscription: me.subscription });
        setBusinesses(me.businesses || []);
        pickActive(me.businesses || []);
      } catch { setUser(false); }
    })();
  }, []);

  const login = async (email, password) => {
    sessionStorage.removeItem("force_onboarding");
    const d = await api.post("/auth/login", { email, password });
    setToken(d.token);
    setUser(d.user); setBusinesses(d.businesses || []); pickActive(d.businesses || []);
    return d;
  };
  const register = async (name, email, password) => {
    const d = await api.post("/auth/register", { name, email, password });
    setToken(d.token);
    setUser(d.user); setBusinesses([]); pickActive([]);
    return d;
  };
  const loginWithGoogle = async (session_id) => {
    sessionStorage.removeItem("force_onboarding");
    const d = await api.post("/auth/google/session", { session_id });
    setToken(d.token);
    setUser(d.user); setBusinesses(d.businesses || []); pickActive(d.businesses || []);
    return d;
  };
  const createBusiness = async (payload) => {
    const b = await api.post("/businesses", payload);
    sessionStorage.removeItem("force_onboarding");
    const list = await api.get("/businesses"); setBusinesses(list);
    localStorage.setItem("business_id", b.id); setActiveBusiness(b.id); setActiveId(b.id);
    return b;
  };
  const switchBusiness = (id) => { sessionStorage.removeItem("force_onboarding"); localStorage.setItem("business_id", id); setActiveBusiness(id); window.location.href = "/"; };
  const deleteCurrentBusiness = async () => {
    await api.del("/business/current");
    localStorage.removeItem("business_id"); setActiveBusiness("");
    window.location.href = "/";
  };
  const logout = async (reason) => {
    try { await api.post("/auth/logout", {}); } catch {}
    setToken(""); localStorage.removeItem("business_id");
    setUser(false); setBusinesses([]); setActiveId(""); setActiveBusiness("");
    if (reason) toast.info(reason);
  };

  // Idle / inactivity auto-logout
  useEffect(() => {
    if (!user) return;
    const bump = () => { lastActivity.current = Date.now(); };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    let warned = false;
    const timer = setInterval(() => {
      const idleMs = Date.now() - lastActivity.current;
      const limit = idleMinutes * 60 * 1000;
      if (idleMs > limit) { clearInterval(timer); logout(`You were signed out after ${idleMinutes} min of inactivity.`); }
      else if (!warned && limit - idleMs <= 30000) { warned = true; toast.warning("You'll be signed out in 30 seconds due to inactivity — move your mouse to stay signed in."); }
      else if (idleMs < 30000) { warned = false; }
    }, 5000);
    return () => { events.forEach((e) => window.removeEventListener(e, bump)); clearInterval(timer); };
  }, [user, idleMinutes]);

  const activeBusiness = businesses.find((b) => b.id === activeId) || null;
  const activeRole = activeBusiness?.role;
  const can = (perm) => {
    if (!activeRole) return false;
    if (activeRole === "owner" || activeRole === "admin") return true;
    const perms = activeBusiness?.permissions || rolePerms(activeRole);
    return perms.includes(perm);
  };

  return (
    <AuthCtx.Provider value={{ user, businesses, activeBusiness, activeId, activeRole, idleMinutes, setIdleMinutes, login, register, loginWithGoogle, logout, createBusiness, switchBusiness, deleteCurrentBusiness, can }}>
      {children}
    </AuthCtx.Provider>
  );
}
